import React from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import ArcGISAuth from '../auth/ArcGISAuth';
import { Toast } from '../components/Toast';
import { ReactComponent as TempoLogo } from '../images/tempo_full_color_DKGray.svg';
import { ReactComponent as ImpactLogo } from '../images/IMPACT_DrkBlu_FULLCOLOR_ICON.svg';

interface LoginPageProps {}

const LoginPage: React.FC<LoginPageProps> = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const accessDenied =
    location.state?.showAccessDenied === true || searchParams.get('error') === 'access_denied';

  // Latched rather than read straight through: the effect below strips the
  // `error` param, which would otherwise take the toast down with it.
  const [showToast, setShowToast] = React.useState(accessDenied);
  const [lastAccessDenied, setLastAccessDenied] = React.useState(accessDenied);
  if (lastAccessDenied !== accessDenied) {
    setLastAccessDenied(accessDenied);
    if (accessDenied) {
      setShowToast(true);
    }
  }

  React.useEffect(() => {
    if (!accessDenied || !searchParams.has('error')) return;
    searchParams.delete('error');
    setSearchParams(searchParams, { replace: true });
  }, [accessDenied, searchParams, setSearchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '20px',
      }}
    >
      <Toast
        open={showToast}
        message="You do not have access to this application. Please contact your administrator."
        severity="error"
        vertical="top"
      />
      <Box
        sx={{
          maxWidth: 500,
          width: '100%',
          p: { xs: 2, sm: 4 },
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <TempoLogo width={'200px'} aria-label="TEMPO" />
        </Box>

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#2E5C8A' }}>
            Tool for Emergency Management and Prioritizing Operations
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#555' }}>
            TEMPO provides critical intelligence to help first responders assess and respond to
            natural disasters including tornadoes, floods, winter storms, earthquakes, wildfires,
            and hurricanes. Using advanced risk assessment algorithms, it analyzes real-time data to
            identify vulnerable areas and delivers daily updates on infrastructure status,
            population impacts, and resource needs across the nation.
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Our system integrates hazard models, infrastructure data, and demographic information to
            enable effective deployment of resources and timely rescue operations.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 3,
              color: '#2E5C8A',
              fontWeight: 500,
              backgroundColor: '#f5f5f5',
              p: 2,
              borderRadius: 1,
            }}
          >
            Note: Access to TEMPO is currently restricted to FEMA personnel and other approved
            emergency management agencies. Please contact your administrator for access.
          </Typography>
        </Box>

        <Box
          sx={{
            mb: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Link
            to="/subscriptions"
            style={{
              color: 'white',
              backgroundColor: '#2E5C8A', // Matching the existing color scheme
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              transition: 'background-color 0.2s ease',
            }}
          >
            Receive TEMPO Alerts
          </Link>
          <ArcGISAuth />
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <a
              href="https://newlighttechnologies.com/impact"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <ImpactLogo
                width="200px"
                style={{
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                }}
                onMouseOver={e => (e.currentTarget.style.opacity = '0.8')}
                onMouseOut={e => (e.currentTarget.style.opacity = '1')}
              />
            </a>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
